import "@/storefront.css";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, resolveImageUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import CustomerAuthModal from "@/components/CustomerAuthModal";
import { getMenuCache, saveMenuCache } from "@/lib/offlineDb";
import {
  ShoppingBag,
  Plus,
  Minus,
  Leaf,
  Search,
  Truck,
  Info,
  Sparkles,
  Clock,
  X,
  User,
  LogIn,
  Award,
  ShieldCheck,
  Flame,
  Star,
  CheckCircle2,
  HelpCircle,
  Coffee,
  ChevronDown,
  ChevronUp,
  Heart,
  Soup,
  Receipt,
  MapPin,
  Phone,
  ExternalLink
} from "lucide-react";
import { DELIVERY_TERMS, FREE_DELIVERY_THRESHOLD, isNightSession } from "@/lib/deliveryTerms";
import { getCartPairings } from "@/lib/pairings";
import PairingShelf from "@/components/PairingShelf";

const HERO = "/velpula_hero.jpg";

const FAQS = [
  {
    q: "How are your South Indian Meals and Curries prepared?",
    a: "All our dishes are freshly prepared daily using pure ingredients and authentic home-style recipes with balanced spices (balanced seasoning) and hearty quantity — just like home food!"
  },
  {
    q: "What is included in the Veg and Non-Veg Full Meals?",
    a: "Our Veg Meals (₹80) include unlimited Rice, Andhra Dal/Pappu, Sambar, Rasam, Vegetable Curry, Curd, Papad and Pickle. Our Non-Veg Meals (₹110) are served with hot spicy Chicken Curry, Rice, Sambar, Rasam, Curd and Papad."
  },
  {
    q: "Do you have Tiffins and Chapathi on the menu?",
    a: "Yes! We serve fresh whole wheat Chapathi at just ₹15 per piece, alongside hot crispy Dosas, Poori (4 Pcs @ ₹40), Idli, Vada, and Mysore Bonda prepared fresh at our live counter."
  },
  {
    q: "Which areas do you deliver to and what is the delivery charge?",
    a: "We deliver across an 8.0 km radius covering Karimguda, Rampally, Keesara, Nagaram, Ghatkesar, Infosys Tech Park / Pocharam, SNIST (Sreenidhi College) & Hostels, and Cherlapally. Delivery starts at ₹20 for 2 km (+₹10/km) and is 100% FREE on orders above ₹499!"
  },
  {
    q: "Where is Velpula Mess located?",
    a: "We are located beside ORR TSSIC Industrial Area, Velpulavanam Venture, Karimguda, Telangana 501302 (Plus Code: FMV7+3H). We are open daily from 6:00 AM to 9:30 PM."
  }
];

export default function Storefront() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cat, setCat] = useState("All");
  const [dietFilter, setDietFilter] = useState("all"); // "all", "veg", "nonveg", "bestseller"
  const [q, setQ] = useState("");
  const [activeTable, setActiveTable] = useState("");
  const [showTerms, setShowTerms] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [variantModalItem, setVariantModalItem] = useState(null);
  const [counterNoticeItem, setCounterNoticeItem] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const isNight = isNightSession();
  const { add, addVariant, setQty, lines, count, subtotal } = useCart();
  const { customer, isCustomerLoggedIn } = useCustomerAuth();

  const fetchMenu = useCallback(async () => {
    setError(null);
    try {
      const cached = await getMenuCache();
      if (Array.isArray(cached) && cached.length) {
        setItems(cached.filter(i => i.available !== false));
        setLoading(false);
      }
    } catch { /* Cache is optional. */ }
    try {
      const res = await api.get("/menu", { params: { only_available: true } });
      if (!Array.isArray(res.data)) throw new Error("Invalid menu response");
      setItems(res.data);
      await saveMenuCache(res.data).catch(() => {});
    } catch {
      setError("Could not refresh the menu. Check your connection and retry before ordering.");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchMenu();

    const searchParams = new URLSearchParams(window.location.search);
    const tableParam = searchParams.get("table");
    if (tableParam) {
      let formattedTable = tableParam.toUpperCase().trim();
      if (!formattedTable.startsWith("T-")) {
        try {
          formattedTable = `T-${parseInt(formattedTable, 10).toString().padStart(2, "0")}`;
        } catch {
          /* use as is */
        }
      }
      sessionStorage.setItem("table_no", formattedTable);
      setActiveTable(formattedTable);
    } else {
      const stored = sessionStorage.getItem("table_no");
      if (stored) setActiveTable(stored);
    }

    const catParam = searchParams.get("category");
    if (catParam) setCat(catParam);
  }, [fetchMenu]);

  const [categoriesOrder, setCategoriesOrder] = useState([]);

  useEffect(() => {
    api.get("/menu/categories")
      .then((r) => {
        if (r.data?.categories) setCategoriesOrder(r.data.categories);
      })
      .catch(() => { });
  }, []);

  const cats = useMemo(() => {
    const set = new Set(items.map((i) => i.category).filter(Boolean));
    const ordered = ["All"];
    const preferredOrder = categoriesOrder.length
      ? categoriesOrder
      : ["Tea", "Tiffins", "Snacks", "Biryani", "Starters", "Tandoori Kebabs", "Currys", "Rotis", "Shawarma", "Sweets", "Fried Rice", "Noodles"];

    preferredOrder.forEach((c) => {
      for (const itemCat of set) {
        if (itemCat.toLowerCase() === c.toLowerCase()) {
          if (!ordered.includes(itemCat)) ordered.push(itemCat);
        }
      }
    });

    for (const itemCat of set) {
      if (!ordered.includes(itemCat)) ordered.push(itemCat);
    }
    return ordered;
  }, [items, categoriesOrder]);

  // Counts for diet filters
  const dietCounts = useMemo(() => {
    const vegCount = items.filter((i) => i.veg).length;
    const nonVegCount = items.filter((i) => !i.veg).length;
    return { all: items.length, veg: vegCount, nonveg: nonVegCount };
  }, [items]);

  const shown = useMemo(() => {
    return items.filter((i) => {
      const matchCat = cat === "All" || i.category?.toLowerCase() === cat.toLowerCase();
      const matchQuery =
        !q ||
        i.name?.toLowerCase().includes(q.toLowerCase()) ||
        i.description?.toLowerCase().includes(q.toLowerCase());
      let matchDiet = true;
      if (dietFilter === "veg") matchDiet = Boolean(i.veg);
      if (dietFilter === "nonveg") matchDiet = !i.veg;
      if (dietFilter === "bestseller")
        matchDiet =
          i.category === "Biryani" ||
          i.category === "Combos" ||
          i.name?.toLowerCase().includes("chai") ||
          i.name?.toLowerCase().includes("dosa");
      return matchCat && matchQuery && matchDiet;
    });
  }, [items, cat, q, dietFilter]);

  const [storeStatus, setStoreStatus] = useState({ is_online_open: true, notice: "" });
  const [googleReviews, setGoogleReviews] = useState(null);

  useEffect(() => {
    api.get("/store/status")
      .then((r) => {
        if (r.data) setStoreStatus(r.data);
      })
      .catch(() => { });

    api.get("/public/google-reviews")
      .then((r) => {
        if (r.data) setGoogleReviews(r.data);
      })
      .catch(() => { });
  }, []);

  const qtyOf = (id) => lines.find((l) => l.item_id === id)?.qty || 0;

  const cartPairings = useMemo(() => {
    return getCartPairings(lines, items, 6);
  }, [lines, items]);

  // Free delivery progress calculation
  const freeDeliveryThreshold = FREE_DELIVERY_THRESHOLD || 499;
  const freeDeliveryProgress = Math.min(100, Math.round((subtotal / freeDeliveryThreshold) * 100));
  const amountNeededForFree = Math.max(0, freeDeliveryThreshold - subtotal);

  return (
    <div className="restaurant-storefront min-h-screen bg-background text-foreground">
      <a className="skip-link" href="#menu">Skip to menu</a>
      {/* Offline Paused Notice Banner */}
      {!storeStatus.is_online_open && (
        <div className="bg-red-600 text-white text-center py-2 px-4 text-xs font-bold uppercase tracking-wider border-b border-foreground flex justify-between items-center max-w-7xl mx-auto">
          <span>⚠️ Online Ordering is temporarily paused right now. Counter Dine-in & Takeaway are active!</span>
          <span className="opacity-90 font-mono text-[10px] hidden sm:inline">Counter Open</span>
        </div>
      )}

      {activeTable && (
        <div className="bg-primary text-primary-foreground text-center py-2 px-4 text-xs font-bold uppercase tracking-widest border-b border-foreground flex justify-between items-center max-w-7xl mx-auto">
          <span>🍽️ Dine-in Self-Ordering Active: <strong>{activeTable}</strong></span>
          <span className="opacity-90 font-mono text-[10px]">Velpula Mess</span>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-foreground bg-background/95 backdrop-blur">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-3 flex items-center gap-6">
          <Link to="/" className="flex items-center gap-3 group" data-testid="brand-logo">
            <img
              src="/logo.webp"
              alt="Velpula Mess Logo"
              width="52"
              height="52"
              className="h-11 w-11 sm:h-13 sm:w-13 object-contain rounded-full border border-amber-900/20 shadow-sm group-hover:scale-105 transition-transform"
            />
            <div className="flex flex-col">
              <span className="serif text-2xl sm:text-3xl font-extrabold tracking-tight text-primary leading-none">
                Velpula Mess
              </span>
              <span className="text-[10px] sm:text-xs uppercase font-bold tracking-widest text-muted-foreground mt-0.5">
                Authentic Home-Style Food
              </span>
            </div>
          </Link>

          <nav className="ml-auto flex items-center gap-3 sm:gap-5 text-xs uppercase tracking-widest">
            {isCustomerLoggedIn ? (
              <Link
                to="/account"
                data-testid="customer-account-link"
                aria-label={`My account: ${customer.name || "Customer"}`}
                className="flex items-center gap-1.5 font-bold text-primary hover:opacity-80 transition"
              >
                <User size={14} className="text-primary" />
                <span className="max-w-[100px] truncate">{customer.name?.split(" ")[0] || "Account"}</span>
                {customer.loyalty_points > 0 && (
                  <span className="hidden sm:inline bg-amber-500/20 text-amber-900 px-1.5 py-0.5 rounded text-[10px] lowercase font-mono">
                    {customer.loyalty_points}pts
                  </span>
                )}
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => setAuthModalOpen(true)}
                data-testid="customer-login-button"
                aria-label="Sign In to customer account"
                className="flex items-center gap-1.5 font-bold hover:text-primary transition"
              >
                <LogIn size={14} />
                <span>Sign In</span>
              </button>
            )}

            <Link
              to="/login"
              data-testid="nav-staff-login"
              aria-label="Staff POS Portal"
              className="flex items-center gap-1 text-[11px] font-bold text-muted-foreground hover:text-primary transition py-1.5 px-2.5 rounded border border-foreground/20 hover:border-primary bg-card hover:bg-muted"
              title="Open Staff & POS Terminal"
            >
              <Receipt size={13} className="text-primary" />
              <span className="hidden sm:inline">Staff POS</span>
            </Link>

            <Link
              to="/checkout"
              data-testid="cart-button"
              aria-label={`View shopping cart with ${count} items, subtotal ${money(subtotal)}`}
              className="hard-shadow flex items-center gap-2 border border-foreground bg-foreground text-background px-4 py-2 text-xs uppercase font-bold tracking-widest hover:bg-primary transition-colors rounded"
            >
              <ShoppingBag size={14} />
              <span className="hidden sm:inline">Cart</span>
              {count > 0 && (
                <span className="bg-primary text-primary-foreground text-[11px] font-bold px-1.5 py-0.2 rounded-full mono">
                  {count}
                </span>
              )}
            </Link>
          </nav>
        </div>

        {/* Free Delivery Dynamic Progress Meter */}
        {count > 0 && (
          <div className="bg-amber-500/15 border-t border-foreground/15 px-5 sm:px-8 py-2">
            <div className="mx-auto max-w-7xl flex flex-wrap items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-2">
                <Truck size={14} className="text-primary" />
                {amountNeededForFree > 0 ? (
                  <span>
                    Add <b className="text-primary font-mono">{money(amountNeededForFree)}</b> more to unlock{" "}
                    <b className="text-emerald-800 uppercase font-black">FREE Express Delivery!</b>
                  </span>
                ) : (
                  <span className="text-emerald-800 font-bold flex items-center gap-1">
                    <CheckCircle2 size={13} className="text-emerald-600" /> 🎉 Congratulations! You've unlocked FREE Delivery!
                  </span>
                )}
              </div>
              <div className="w-full sm:w-48 bg-muted border border-foreground/30 h-2 rounded-full overflow-hidden">
                <div
                  className="bg-primary h-full transition-all duration-300 rounded-full"
                  style={{ width: `${freeDeliveryProgress}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content Landmark */}
      <main id="main-content">
        {/* Hero Section */}
        <section className="relative border-b border-foreground bg-card overflow-hidden">
        <div className="mx-auto grid max-w-7xl gap-8 px-5 sm:px-8 py-14 sm:py-20 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 border border-foreground/30 bg-amber-500/10 px-3 py-1 text-xs uppercase tracking-widest font-bold text-amber-900 rounded-full">
              <Sparkles size={13} className="text-amber-600" />
              <span>Authentic Home-Style Meals &amp; Biryani</span>
            </div>
            <h1 className="serif text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05]">
              Authentic South Indian Meals, Biryani &amp; Tiffins
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
              Freshly prepared with pure ingredients, authentic home-style recipes, and traditional spices. Served piping hot to your table and doorstep.
            </p>

            <div className="flex flex-wrap gap-4 pt-2">
              <a
                href="#menu"
                data-testid="explore-menu-cta"
                className="hard-shadow border border-foreground bg-primary text-primary-foreground px-7 py-3.5 text-xs font-bold uppercase tracking-widest hover:bg-foreground hover:text-background transition-colors"
              >
                Order Online Now
              </a>
              <Link
                to="/track"
                className="border border-foreground bg-card px-6 py-3.5 text-xs font-bold uppercase tracking-widest hover:bg-muted transition-colors flex items-center gap-2"
              >
                <Clock size={14} />
                <span>Live Tracker</span>
              </Link>
            </div>

            {/* Quick stats / trust chips */}
            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-foreground/15 max-w-lg">
              <div>
                <div className="serif text-2xl font-black text-primary">Meals</div>
                <div className="text-[11px] uppercase font-bold text-muted-foreground tracking-wider">Tiffins & biryani</div>
              </div>
              <div>
                <div className="serif text-2xl font-black text-foreground">Your choice</div>
                <div className="text-[11px] uppercase font-bold text-muted-foreground tracking-wider">Dine in or delivery</div>
              </div>
              <div>
                <div className="serif text-2xl font-black text-emerald-700">Track</div>
                <div className="text-[11px] uppercase font-bold text-muted-foreground tracking-wider">Order updates</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="hard-shadow aspect-[4/3] sm:aspect-square overflow-hidden border-2 border-foreground bg-muted relative rounded-2xl">
              <picture>
                <source media="(max-width: 640px)" srcSet="/velpula_hero_mobile.webp" type="image/webp" />
                <source srcSet="/velpula_hero.webp" type="image/webp" />
                <img
                  src="/velpula_hero_mobile.webp"
                  alt="Velpula Mess Meals & Biryani feast"
                  className="h-full w-full object-cover"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  width="480"
                  height="480"
                />
              </picture>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-6 text-white">
                <span className="text-xs uppercase font-bold tracking-widest text-amber-300">Daily Special</span>
                <p className="serif text-2xl font-bold mt-1">Special Veg &amp; Non-Veg Meals</p>
                <p className="text-xs text-white/80 mt-0.5">Authentic recipes made fresh every day</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee Banner */}
      <div className="border-b border-foreground bg-secondary py-3 text-secondary-foreground overflow-hidden">
        <div className="marquee-track flex whitespace-nowrap">
          {[...Array(2)].map((_, idx) => (
            <div key={idx} className="flex shrink-0 items-center">
              {[
                "🍲 Authentic Veg & Non-Veg Meals",
                "🔥 Special Dum Biryani",
                "🥞 Crispy Tiffins & Dosas",
                "🚚 Fast & Fresh Door Delivery",
                "✨ Pure & Traditional Quality",
              ].map((t) => (
                <span key={t} className="overline px-6 text-secondary-foreground/90 font-bold">
                  {t} —
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* 4 Heritage Trust Pillars */}
      <section className="border-b border-foreground bg-muted/30 py-8 px-5 sm:px-8">
        <h2 className="sr-only">Why Food Lovers Choose Velpula Mess</h2>
        <div className="mx-auto max-w-7xl grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <div className="border border-foreground/30 bg-card p-4 rounded-xl space-y-2">
            <div className="w-9 h-9 rounded-lg bg-amber-500/20 text-amber-900 flex items-center justify-center">
              <Flame size={18} />
            </div>
            <h3 className="font-bold text-sm">Homely Cooking</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Authentic home-style recipes with balanced spices &amp; pure ingredients.
            </p>
          </div>

          <div className="border border-foreground/30 bg-card p-4 rounded-xl space-y-2">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-900 flex items-center justify-center">
              <ShieldCheck size={18} />
            </div>
            <h3 className="font-bold text-sm">Choose your meal</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Browse available dishes and select the portion that suits your appetite.
            </p>
          </div>

          <div className="border border-foreground/30 bg-card p-4 rounded-xl space-y-2">
            <div className="w-9 h-9 rounded-lg bg-blue-500/20 text-blue-900 flex items-center justify-center">
              <Coffee size={18} />
            </div>
            <h3 className="font-bold text-sm">Hot Tiffins &amp; Chapathi</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Whole wheat chapathi at ₹15, crispy dosas and fluffy poori combos.
            </p>
          </div>

          <div className="border border-foreground/30 bg-card p-4 rounded-xl space-y-2">
            <div className="w-9 h-9 rounded-lg bg-purple-500/20 text-purple-900 flex items-center justify-center">
              <Truck size={18} />
            </div>
            <h3 className="font-bold text-sm">Fast 8km Delivery</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Dispatched hot in heat-sealed containers across Karimguda &amp; surroundings.
            </p>
          </div>
        </div>
      </section>

      {/* Main Menu Section */}
      <section id="menu" className="mx-auto max-w-7xl px-5 sm:px-8 py-12 sm:py-16">
        <div className="flex flex-wrap items-end gap-6 justify-between border-b border-foreground/20 pb-6">
          <div>
            <p className="overline text-primary">Traditional Home-Style Menu</p>
            <h2 className="serif text-4xl sm:text-5xl font-extrabold tracking-tight mt-1">Explore Our Dishes</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Showing {shown.length} dishes · Choose from authentic South Indian Meals, Biryani, Tiffins &amp; Currys
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            {/* Search Input */}
            <div className="relative flex-1 sm:w-72">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="menu-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search meals, biryani, dosa, poori..."
                className="w-full border border-foreground bg-card pl-9 pr-3 py-2.5 text-xs outline-none focus:ring-2 focus:ring-foreground rounded"
              />
            </div>
          </div>
        </div>

        {/* Veg / Non-Veg / Bestseller Filter Switcher */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setDietFilter("all")}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded border transition ${dietFilter === "all"
                  ? "bg-foreground text-background border-foreground"
                  : "bg-card text-muted-foreground border-foreground/30 hover:border-foreground"
                }`}
            >
              All Items ({dietCounts.all})
            </button>

            <button
              onClick={() => setDietFilter("veg")}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded border flex items-center gap-1.5 transition ${dietFilter === "veg"
                  ? "bg-emerald-700 text-white border-emerald-800"
                  : "bg-emerald-50 text-emerald-900 border-emerald-300 hover:bg-emerald-100"
                }`}
            >
              <span className="w-2 h-2 rounded-full bg-emerald-600 inline-block" />
              Pure Veg ({dietCounts.veg})
            </button>

            <button
              onClick={() => setDietFilter("nonveg")}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded border flex items-center gap-1.5 transition ${dietFilter === "nonveg"
                  ? "bg-red-700 text-white border-red-800"
                  : "bg-red-50 text-red-900 border-red-300 hover:bg-red-100"
                }`}
            >
              <span className="w-2 h-2 rounded-full bg-red-600 inline-block" />
              Non-Veg ({dietCounts.nonveg})
            </button>

            <button
              onClick={() => setDietFilter("bestseller")}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded border flex items-center gap-1.5 transition ${dietFilter === "bestseller"
                  ? "bg-amber-600 text-white border-amber-700"
                  : "bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100"
                }`}
            >
              <Star size={12} className="fill-amber-500 text-amber-500" />
              Explore favourites
            </button>
          </div>

          <button
            onClick={() => setShowTerms(true)}
            aria-label="Delivery Terms (8km)"
            className="text-[11px] font-bold text-primary hover:underline flex items-center gap-1"
          >
            <Info size={13} /> Delivery Terms (8km)
          </button>
        </div>

        {/* Category Filter Pills */}
        <div className="mt-4 flex gap-2 overflow-x-auto pb-2 scrollbar-none" data-testid="category-filters">
          {cats.map((c) => (
            <button
              key={c}
              data-testid={`category-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setCat(c)}
              aria-label={`Filter by category ${c}`}
              className={`border border-foreground px-4 py-2 text-xs uppercase tracking-wider font-bold whitespace-nowrap rounded transition-colors ${cat === c ? "bg-foreground text-background" : "bg-card hover:bg-muted text-muted-foreground"
                }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="menu-loading-skeleton">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="border border-foreground/30 bg-card animate-pulse flex flex-col rounded-xl overflow-hidden">
                <div className="aspect-[4/3] bg-muted/80 border-b border-foreground/20" />
                <div className="p-5 flex flex-col flex-1 space-y-3">
                  <div className="h-6 bg-muted rounded w-3/4" />
                  <div className="h-4 bg-muted/60 rounded w-full" />
                  <div className="h-4 bg-muted/60 rounded w-2/3" />
                  <div className="pt-4 flex justify-between items-center border-t border-foreground/10">
                    <div className="h-6 bg-muted rounded w-16" />
                    <div className="h-8 bg-muted rounded w-20" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="mt-8 text-center p-8 border border-destructive/40 bg-destructive/10 rounded-xl" data-testid="menu-error">
            <p className="text-destructive font-bold">{error}</p>
            <button
              onClick={() => fetchMenu(0)}
              aria-label="Retry loading menu"
              className="mt-3 border border-foreground bg-primary text-primary-foreground px-4 py-2 text-xs uppercase font-bold tracking-wider rounded"
            >
              Tap to Retry
            </button>
          </div>
        ) : (
          <>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="menu-grid">
              {shown.map((i, idx) => {
                const currentQty = qtyOf(i.id);
                const hasPortions = Array.isArray(i.variants) && i.variants.length > 0;
                const isCounterItem =
                  i.category === "Chai & Beverages" ||
                  i.category === "Snacks" ||
                  i.name?.toLowerCase().includes("chai") ||
                  i.name?.toLowerCase().includes("samosa") ||
                  i.name?.toLowerCase().includes("biscuit");

                return (
                  <article
                    key={i.id}
                    data-testid={`menu-item-${i.id}`}
                    className="border border-foreground/40 bg-card flex flex-col rounded-xl overflow-hidden hover:shadow-lg transition-all duration-200 rise"
                    style={{ animationDelay: `${Math.min(idx, 8) * 45}ms` }}
                  >
                    {/* Food Photo Card */}
                    <div className="aspect-[4/3] overflow-hidden border-b border-foreground/30 bg-muted relative">
                      {i.image_url ? (
                        <img
                          src={resolveImageUrl(i.image_url)}
                          alt={i.name}
                          loading="lazy"
                          decoding="async"
                          width="400"
                          height="300"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            const fallback = e.currentTarget.parentElement?.querySelector(".img-fallback");
                            if (fallback) fallback.style.display = "flex";
                          }}
                          className="h-full w-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      ) : null}

                      <div
                        className="img-fallback aspect-[4/3] w-full h-full border-b border-foreground/20 bg-muted items-center justify-center flex flex-col text-muted-foreground"
                        style={{ display: i.image_url ? "none" : "flex" }}
                      >
                        <span className="serif text-5xl text-foreground/20 font-black">{i.name ? i.name[0] : "☕"}</span>
                        <span className="text-[10px] uppercase font-bold tracking-wider mt-1">{i.category}</span>
                      </div>

                      {/* Veg / Non-Veg Indicator Badge */}
                      <div className="absolute top-3 left-3 bg-background/90 backdrop-blur px-2 py-1 rounded-md border border-foreground/30 flex items-center gap-1.5 shadow-sm">
                        <span
                          className={`w-2.5 h-2.5 rounded-full ${i.veg ? "bg-emerald-600" : "bg-red-600"
                            }`}
                        />
                        <span className="text-[10px] font-bold uppercase tracking-wider">
                          {i.veg ? "Veg" : "Non-Veg"}
                        </span>
                      </div>

                      {/* Live Counter Pill on Image */}
                      {isCounterItem ? (
                        <span className="absolute top-3 right-3 bg-amber-900/90 text-amber-200 border border-amber-500/40 px-2.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider backdrop-blur flex items-center gap-1 shadow">
                          <Coffee size={11} className="text-amber-400" />
                          <span>Live Counter</span>
                        </span>
                      ) : (
                        <span className="absolute top-3 right-3 bg-foreground/80 text-background px-2.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider backdrop-blur">
                          {i.category}
                        </span>
                      )}
                    </div>

                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="text-xl sm:text-2xl font-bold tracking-tight leading-tight">{i.name}</h3>
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground leading-relaxed flex-1 line-clamp-2">
                        {i.description || `${i.name} prepared fresh with authentic ingredients.`}
                      </p>

                      <div className="mt-5 flex items-center justify-between pt-3 border-t border-foreground/10">
                        <div>
                          {hasPortions ? (
                            <div>
                              <div className="mono text-base font-black text-foreground">
                                ₹{i.variants[0].price} <span className="text-xs font-normal text-muted-foreground">/ ₹{i.variants[1].price}</span>
                              </div>
                              <span className="text-[10px] uppercase font-bold text-primary">
                                {i.variants[0].name} &amp; {i.variants[1].name}
                              </span>
                            </div>
                          ) : (
                            <span className="mono text-lg font-black text-foreground">
                              {i.price > 0 ? money(i.price) : "Contact for Price"}
                            </span>
                          )}
                        </div>

                        {activeTable && isCounterItem ? (
                          <button
                            data-testid={`counter-notice-${i.id}`}
                            onClick={() => setCounterNoticeItem(i)}
                            aria-label={`Order ${i.name} at counter`}
                            className="border border-amber-600 bg-amber-500/15 text-amber-950 px-3.5 py-2 text-xs font-bold uppercase tracking-wider hover:bg-amber-600 hover:text-white transition-colors flex items-center gap-1.5 rounded active:scale-95 shadow-sm"
                          >
                            <Coffee size={13} className="text-amber-700 group-hover:text-white" />
                            <span>Order at Counter</span>
                          </button>
                        ) : hasPortions ? (
                          <button
                            data-testid={`add-to-cart-${i.id}`}
                            onClick={() => setVariantModalItem(i)}
                            aria-label={`Select portions for ${i.name}`}
                            className="border border-foreground bg-primary text-primary-foreground px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-foreground hover:text-background transition-colors flex items-center gap-1.5 rounded active:scale-95"
                          >
                            <span>Add</span>
                            <Plus size={13} />
                          </button>
                        ) : currentQty === 0 ? (
                          <button
                            data-testid={`add-to-cart-${i.id}`}
                            onClick={() => add(i)}
                            aria-label={`Add ${i.name} to cart`}
                            className="border border-foreground bg-primary text-primary-foreground px-5 py-2 text-xs uppercase font-bold tracking-widest hover:bg-foreground transition-colors rounded active:scale-95 shadow-sm"
                          >
                            Add
                          </button>
                        ) : (
                          <div className="flex items-center border-2 border-foreground rounded-lg bg-card shadow-sm">
                            <button
                              data-testid={`dec-${i.id}`}
                              onClick={() => setQty(i.id, currentQty - 1)}
                              aria-label={`Decrease quantity of ${i.name}`}
                              className="px-2.5 py-1.5 hover:bg-muted transition text-foreground"
                            >
                              <Minus size={13} />
                            </button>
                            <span data-testid={`qty-${i.id}`} className="mono px-3 text-xs font-black text-foreground">
                              {currentQty}
                            </span>
                            <button
                              data-testid={`inc-${i.id}`}
                              onClick={() => add(i)}
                              aria-label={`Increase quantity of ${i.name}`}
                              className="px-2.5 py-1.5 hover:bg-muted transition text-foreground"
                            >
                              <Plus size={13} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            {shown.length === 0 && (
              <div className="mt-12 text-center p-8 border border-foreground/30 bg-card rounded-xl" data-testid="menu-empty">
                <p className="text-base font-bold tracking-tight">No dishes found</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {q ? `No items match "${q}". Try clearing your search.` : "No items available under selected filters."}
                </p>
                <button
                  onClick={() => {
                    setQ("");
                    setCat("All");
                    setDietFilter("all");
                  }}
                  aria-label="Reset all search and category filters"
                  className="mt-4 border border-foreground bg-primary text-primary-foreground px-5 py-2 text-xs uppercase font-bold tracking-wider rounded"
                >
                  Reset Filters
                </button>
              </div>
            )}
          </>
        )}

        {/* Pairing Suggestions Shelf */}
        {lines.length > 0 && cartPairings.length > 0 && (
          <div className="mt-12">
            <PairingShelf
              pairings={cartPairings}
              title="You will love pairing your order with"
              subtitle="Delicious snacks, accompaniments & beverages that complete your meal"
              onSelectVariantItem={(dish) => setVariantModalItem(dish)}
            />
          </div>
        )}
      </section>

      {/* Live Google Reviews & Ratings Section */}
      <section id="reviews" className="border-t border-foreground/20 bg-card py-14 px-5 sm:px-8">
        <div className="mx-auto max-w-7xl space-y-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs uppercase tracking-widest font-bold text-amber-700 dark:text-amber-300 rounded-full">
                <Star size={13} className="fill-amber-500 text-amber-500" />
                <span>Verified Google Reviews</span>
              </div>
              <h2 className="serif text-3xl sm:text-4xl font-extrabold tracking-tight">
                Loved by Karimguda Diners & Families
              </h2>
              <p className="text-sm text-muted-foreground max-w-xl">
                Real customer experiences enjoying authentic Telangana home-style meals, tiffins, and Biryani at Velpula Mess.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3.5 shrink-0">
              <div className="border border-foreground/20 bg-muted/20 px-4 py-2.5 rounded-xl flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center font-bold text-sm text-[#4285F4]">
                  G
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="font-extrabold text-sm mono">5.0</span>
                    <div className="flex text-amber-500">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={12} className="fill-amber-500" />
                      ))}
                    </div>
                  </div>
                  <span className="text-[10px] text-muted-foreground font-semibold">
                    Google Business Profile
                  </span>
                </div>
              </div>

              <a
                href={googleReviews?.write_review_url || "https://search.google.com/local/writereview?placeid=ChIJB5UNra13yzsRgVc8MGloK_E"}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-accent px-4 py-2.5 rounded-xl font-bold text-xs sm:text-sm flex items-center gap-2"
              >
                <Star size={14} className="fill-current" />
                <span>Review Us on Google</span>
                <ExternalLink size={13} />
              </a>
            </div>
          </div>

          {/* Review Cards Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 pt-2">
            {(googleReviews?.reviews && googleReviews.reviews.length > 0) ? (
              googleReviews.reviews.map((rev, idx) => (
                <div key={idx} className="border border-foreground/20 bg-background/60 p-5 rounded-2xl space-y-3 flex flex-col justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {rev.profile_photo_url ? (
                          <img src={rev.profile_photo_url} alt={rev.author_name} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold text-xs">
                            {rev.author_name.charAt(0)}
                          </div>
                        )}
                        <div>
                          <p className="font-bold text-xs sm:text-sm leading-tight">{rev.author_name}</p>
                          <span className="text-[10px] text-muted-foreground">{rev.relative_time}</span>
                        </div>
                      </div>
                      <div className="flex text-amber-500">
                        {[...Array(rev.rating || 5)].map((_, i) => (
                          <Star key={i} size={12} className="fill-amber-500" />
                        ))}
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm text-foreground/80 leading-relaxed italic">
                      "{rev.text}"
                    </p>
                  </div>
                  <div className="pt-2 border-t border-foreground/10 flex items-center gap-1.5 text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">
                    <CheckCircle2 size={12} />
                    <span>Verified Google Review</span>
                  </div>
                </div>
              ))
            ) : (
              <>
                <div className="border border-foreground/20 bg-background/60 p-5 rounded-2xl space-y-3 flex flex-col justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-xs sm:text-sm">Ramesh K.</p>
                        <span className="text-[10px] text-muted-foreground">Karimguda Regular Patron</span>
                      </div>
                      <div className="flex text-amber-500">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} size={12} className="fill-amber-500" />
                        ))}
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm text-foreground/80 leading-relaxed italic">
                      "Authentic Telangana meals served piping hot on banana leaf. Sambar, rasam, and chicken curry taste completely homemade. The best mess near ORR!"
                    </p>
                  </div>
                  <div className="pt-2 border-t border-foreground/10 flex items-center gap-1.5 text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">
                    <CheckCircle2 size={12} />
                    <span>Dine-in Customer Review</span>
                  </div>
                </div>

                <div className="border border-foreground/20 bg-background/60 p-5 rounded-2xl space-y-3 flex flex-col justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-xs sm:text-sm">Suresh Reddy</p>
                        <span className="text-[10px] text-muted-foreground">Online Delivery Customer</span>
                      </div>
                      <div className="flex text-amber-500">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} size={12} className="fill-amber-500" />
                        ))}
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm text-foreground/80 leading-relaxed italic">
                      "Ordered Veg Meals and Chapathis through the website. Delivered within 25 minutes hot and fresh in Karimguda. Very neat eco-friendly packaging."
                    </p>
                  </div>
                  <div className="pt-2 border-t border-foreground/10 flex items-center gap-1.5 text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">
                    <CheckCircle2 size={12} />
                    <span>Online Delivery Review</span>
                  </div>
                </div>

                <div className="border border-foreground/20 bg-background/60 p-5 rounded-2xl space-y-3 flex flex-col justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-xs sm:text-sm">Priya Sharma</p>
                        <span className="text-[10px] text-muted-foreground">Family Weekend Diner</span>
                      </div>
                      <div className="flex text-amber-500">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} size={12} className="fill-amber-500" />
                        ))}
                      </div>
                    </div>
                    <p className="text-xs sm:text-sm text-foreground/80 leading-relaxed italic">
                      "Pure home-style cooking with no artificial soda or colors. Non-veg meals and Biryani on Sundays are exceptional value for money."
                    </p>
                  </div>
                  <div className="pt-2 border-t border-foreground/10 flex items-center gap-1.5 text-[11px] text-emerald-600 dark:text-emerald-400 font-semibold">
                    <CheckCircle2 size={12} />
                    <span>Weekend Family Review</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Interactive FAQ Accordion */}
      <section className="border-t border-foreground/20 bg-muted/20 py-14 px-5 sm:px-8">
        <div className="mx-auto max-w-4xl space-y-6">
          <div className="text-center space-y-2">
            <p className="overline text-primary">Got Questions?</p>
            <h2 className="serif text-3xl sm:text-4xl font-extrabold tracking-tight">Frequently Asked Questions</h2>
            <p className="text-xs text-muted-foreground">Everything you need to know about ordering and delivery</p>
          </div>

          <div className="space-y-3 pt-4">
            {FAQS.map((faq, index) => {
              const isOpen = openFaq === index;
              return (
                <div
                  key={index}
                  className="border border-foreground/30 bg-card rounded-xl overflow-hidden transition"
                >
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : index)}
                    aria-label={`Toggle FAQ: ${faq.q}`}
                    aria-expanded={isOpen}
                    className="w-full p-4 sm:p-5 text-left flex items-center justify-between gap-4 font-bold text-sm sm:text-base hover:bg-muted/40 transition"
                  >
                    <span>{faq.q}</span>
                    {isOpen ? <ChevronUp size={18} className="shrink-0 text-primary" /> : <ChevronDown size={18} className="shrink-0 text-muted-foreground" />}
                  </button>
                  {isOpen && (
                    <div className="px-4 sm:px-5 pb-5 pt-1 text-xs sm:text-sm text-muted-foreground leading-relaxed border-t border-foreground/10 bg-background/50">
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Floating Bottom Cart Bar for Mobile */}
      {count > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-3 sm:hidden bg-background/90 backdrop-blur border-t border-foreground animate-slideUp">
          <Link
            to="/checkout"
            data-testid="sticky-checkout-bar"
            aria-label={`Proceed to checkout with ${count} items, subtotal ${money(subtotal)}`}
            className="w-full border-2 border-foreground bg-foreground text-background py-3.5 px-5 flex items-center justify-between rounded-xl hard-shadow"
          >
            <div className="flex items-center gap-2.5">
              <ShoppingBag size={18} className="text-amber-400" />
              <span className="mono text-sm font-bold">
                {count} items · {money(subtotal)}
              </span>
            </div>
            <span className="text-xs uppercase font-bold tracking-widest text-amber-300">
              Checkout →
            </span>
          </Link>
        </div>
      )}

      {/* Location, Google Map & Service Area Section */}
      <section className="border-t border-foreground bg-card py-14 px-5 sm:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-5">
              <div className="inline-flex items-center gap-2 border border-primary/30 bg-primary/10 px-3 py-1 text-xs uppercase tracking-widest font-bold text-primary rounded-full">
                <MapPin size={13} className="text-primary" />
                <span>Karimguda, Telangana</span>
              </div>
              <h2 className="serif text-3xl sm:text-4xl font-extrabold tracking-tight">
                Visit Velpula Mess or Order Online
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Enjoy hot, authentic home-style food at our restaurant or have it delivered fast to your doorstep. Located conveniently beside ORR TSSIC Industrial Area.
              </p>

              <div className="space-y-3 pt-2 text-xs">
                <div className="flex items-start gap-2.5">
                  <MapPin size={16} className="text-primary shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-foreground">Dine-in &amp; Kitchen Address:</strong>
                    <p className="text-muted-foreground">
                      Beside ORR TSSIC Industrial Area, Velpulavanam Venture, Karimguda, Telangana 501302
                    </p>
                    <span className="text-[11px] mono font-semibold text-primary">Plus Code: FMV7+3H Karimguda, Telangana</span>
                  </div>
                </div>

                <div className="flex items-center gap-2.5">
                  <Clock size={16} className="text-primary shrink-0" />
                  <div>
                    <strong className="text-foreground">Timings: </strong>
                    <span className="text-muted-foreground">6:00 AM – 9:30 PM (All 7 Days Open)</span>
                  </div>
                </div>

                <div className="flex items-center gap-2.5">
                  <Phone size={16} className="text-primary shrink-0" />
                  <div>
                    <strong className="text-foreground">Helpline: </strong>
                    <a href="tel:+919848770865" className="font-bold text-primary hover:underline">
                      +91 98487 70865 (098487 70865)
                    </a>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <p className="text-xs uppercase font-bold tracking-wider text-foreground mb-2">
                  Serving Up to 8.0 KM Around:
                </p>
                <div className="flex flex-wrap gap-2 text-xs">
                  {[
                    "Karimguda",
                    "Rampally",
                    "Keesara",
                    "Nagaram",
                    "Ghatkesar",
                    "Infosys Pocharam / Mindspace",
                    "SNIST (Sreenidhi College & Hostels)",
                    "Cherlapally Station & Industrial Area"
                  ].map((zone) => (
                    <span
                      key={zone}
                      className="border border-foreground/20 bg-background px-2.5 py-1 rounded-md text-[11px] font-semibold text-foreground/90 shadow-sm"
                    >
                      📍 {zone}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-3 pt-3">
                <a
                  href="https://www.google.com/maps/place/velpula+meals+veg+%26+non+veg/@17.4959898,78.649416,14.61z/data=!4m6!3m5!1s0x3bcb77adad0d9507:0xf12b6869303c5781!8m2!3d17.4927058!4d78.6639096!16s%2Fg%2F11zdhrj32z"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Open in Google Maps"
                  className="hard-shadow border border-foreground bg-primary text-primary-foreground px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-foreground hover:text-background transition-colors flex items-center gap-2 rounded"
                >
                  <MapPin size={14} />
                  <span>Open in Google Maps</span>
                  <ExternalLink size={12} />
                </a>
                <a
                  href="tel:+919848770865"
                  aria-label="Call 098487 70865"
                  className="border border-foreground bg-background px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-muted transition-colors flex items-center gap-2 rounded"
                >
                  <Phone size={14} className="text-primary" />
                  <span>Call 098487 70865</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="border-2 border-foreground bg-background p-6 rounded-2xl hard-shadow space-y-4">
                <div className="flex items-center justify-between border-b border-foreground/15 pb-3">
                  <div className="flex items-center gap-2">
                    <Truck size={18} className="text-primary" />
                    <span className="font-bold text-sm uppercase tracking-wider">Fast Delivery Tiers</span>
                  </div>
                  <span className="text-[10px] bg-emerald-500/20 text-emerald-900 font-bold px-2 py-0.5 rounded">
                    Max 8.0 KM
                  </span>
                </div>

                <div className="space-y-2 text-xs">
                  <div className="flex justify-between items-center py-1.5 border-b border-foreground/10">
                    <span className="font-medium">0 to 2.0 KM (Karimguda)</span>
                    <span className="font-mono font-bold text-primary">₹20</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 border-b border-foreground/10">
                    <span className="font-medium">2 to 4.0 KM (Rampally / Pocharam)</span>
                    <span className="font-mono font-bold text-primary">₹30 – ₹40</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 border-b border-foreground/10">
                    <span className="font-medium">4 to 6.0 KM (Ghatkesar / SNIST / Keesara)</span>
                    <span className="font-mono font-bold text-primary">₹50 – ₹60</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 border-b border-foreground/10">
                    <span className="font-medium">6 to 8.0 KM (Cherlapally / Nagaram)</span>
                    <span className="font-mono font-bold text-primary">₹70 – ₹80</span>
                  </div>
                </div>

                <div className="bg-emerald-500/10 border border-emerald-500/30 p-3 rounded-xl text-center text-xs text-emerald-950 font-bold">
                  🎉 FREE Express Delivery on all orders above ₹499!
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-foreground bg-secondary text-secondary-foreground">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14 grid gap-8 sm:grid-cols-4">
          <div className="sm:col-span-2 space-y-3">
            <div className="flex items-center gap-3">
              <img
                src="/logo.webp"
                alt="Velpula Mess Logo"
                width="40"
                height="40"
                className="h-10 w-10 object-contain rounded-full border border-secondary-foreground/20"
              />
              <p className="serif text-2xl font-black tracking-tight">Velpula Mess</p>
            </div>
            <p className="text-xs text-secondary-foreground/75 leading-relaxed max-w-md">
              Authentic Home-Style South Indian Meals (Veg ₹80, Non-Veg ₹110), Biryani, Tiffins &amp; Chapathi (₹15). Pure taste with less masala and generous quantity!
            </p>
            <div className="space-y-1 text-xs text-secondary-foreground/90 mono pt-2">
              <div>Address: <span className="text-secondary-foreground/80">Beside ORR TSSIC Industrial Area, Karimguda, Telangana 501302</span></div>
              <div>Phone: <a href="tel:+919848770865" className="font-bold text-secondary-foreground hover:underline">+91 98487 70865 (098487 70865)</a></div>
              <div>Email: <a href="mailto:contact@velpulamess.com" className="text-secondary-foreground/80 hover:underline">contact@velpulamess.com</a></div>
            </div>
          </div>

          <div className="text-sm">
            <p className="overline text-secondary-foreground/60 font-bold">Quick Links</p>
            <div className="mt-3 space-y-2">
              <Link to="/account" className="block text-xs underline underline-offset-4 hover:text-foreground">My Account &amp; Points</Link>
              <Link to="/track" className="block text-xs underline underline-offset-4 hover:text-foreground">Track Live Order</Link>
              <Link to="/contact" className="block text-xs underline underline-offset-4 hover:text-foreground">Store Location &amp; Dine-in</Link>
              <Link to="/login" className="block text-xs underline underline-offset-4 hover:text-foreground">Staff &amp; Admin Portal</Link>
            </div>
          </div>

          <div className="text-sm">
            <p className="overline text-secondary-foreground/60 font-bold">Policies &amp; Legal</p>
            <div className="mt-3 space-y-2">
              <Link to="/terms" className="block text-xs underline underline-offset-4 hover:text-foreground">Terms &amp; Conditions</Link>
              <Link to="/privacy" className="block text-xs underline underline-offset-4 hover:text-foreground">Privacy Policy</Link>
              <Link to="/refund-policy" className="block text-xs underline underline-offset-4 hover:text-foreground">Refund &amp; Cancellation</Link>
              <Link to="/shipping-policy" className="block text-xs underline underline-offset-4 hover:text-foreground">Shipping &amp; Delivery</Link>
            </div>
          </div>
        </div>

        <div className="border-t border-secondary-foreground/20 text-center py-4 text-xs font-bold uppercase tracking-widest text-secondary-foreground/60 flex flex-col sm:flex-row items-center justify-between max-w-7xl mx-auto px-5 gap-2">
          <span>© {new Date().getFullYear()} Velpula Mess. All Rights Reserved.</span>
          <span>Karimguda · Rampally · Ghatkesar · Pocharam · Cherlapally</span>
        </div>
      </footer>

      {/* Customer Login / Register Modal */}
      <CustomerAuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onSuccess={() => setAuthModalOpen(false)}
      />

      {/* Portion Selection Modal */}
      {variantModalItem && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-card border-2 border-foreground max-w-md w-full p-6 space-y-5 hard-shadow rounded-2xl animate-in zoom-in-95 duration-200">
            <div className="flex items-start justify-between border-b border-foreground/20 pb-3">
              <div className="flex items-center gap-3">
                {variantModalItem.image_url ? (
                  <img
                    src={variantModalItem.image_url}
                    alt={variantModalItem.name}
                    className="w-14 h-14 rounded-xl object-cover border border-foreground/30 shrink-0"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-primary/10 text-primary border border-foreground/20 flex items-center justify-center font-bold text-lg">
                    {variantModalItem.name[0]}
                  </div>
                )}
                <div>
                  <h3 className="text-xl font-bold tracking-tight leading-tight">
                    {variantModalItem.name}
                  </h3>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mt-0.5">
                    {variantModalItem.category}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setVariantModalItem(null)}
                aria-label="Close portion modal"
                className="text-muted-foreground hover:text-foreground text-sm font-bold border border-foreground/20 p-1.5 rounded hover:bg-muted"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Select Portion Size:
              </p>
              <div className="grid grid-cols-2 gap-3">
                {variantModalItem.variants?.map((v) => (
                  <button
                    key={v.name}
                    type="button"
                    onClick={() => {
                      addVariant(variantModalItem, v);
                      setVariantModalItem(null);
                    }}
                    aria-label={`Select ${v.name} for ₹${v.price}`}
                    className="border-2 border-foreground bg-primary hover:bg-foreground text-primary-foreground hover:text-background p-4 rounded-xl flex flex-col items-center justify-center gap-1 transition active:scale-95 shadow group"
                  >
                    <span className="text-sm font-black uppercase tracking-wider">
                      {v.name}
                    </span>
                    <span className="font-mono text-base font-bold">
                      ₹{v.price}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={() => setVariantModalItem(null)}
              aria-label="Cancel portion selection"
              className="w-full border border-foreground py-2.5 text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground rounded"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Live Tea & Snacks Counter Notice Modal */}
      {counterNoticeItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="w-full max-w-md border-2 border-foreground bg-background p-6 hard-shadow space-y-4 rounded-2xl relative">
            <div className="flex justify-between items-start border-b border-foreground/15 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-amber-500/20 text-amber-900 flex items-center justify-center">
                  <Coffee size={18} />
                </div>
                <div>
                  <p className="text-[10px] uppercase font-black tracking-widest text-amber-700">Live Counter Service</p>
                  <h3 className="text-xl font-bold tracking-tight">{counterNoticeItem.name}</h3>
                </div>
              </div>
              <button
                onClick={() => setCounterNoticeItem(null)}
                aria-label="Close counter notice"
                className="border border-foreground/30 p-1 hover:bg-muted rounded"
              >
                <X size={16} />
              </button>
            </div>

            <div className="border border-amber-500/30 bg-amber-500/10 p-4 rounded-xl space-y-2 text-xs text-amber-950">
              <p className="font-bold flex items-center gap-1.5 text-amber-900">
                <span>🍽️</span> Fresh Live Counter Item
              </p>
              <p className="leading-relaxed opacity-95">
                Freshly prepared and served hot directly at our <strong>Live Counter</strong>.
              </p>
              <div className="pt-2 border-t border-amber-500/20 flex justify-between items-center text-[11px] font-mono">
                <span>Item Price:</span>
                <span className="font-bold text-sm text-foreground">₹{counterNoticeItem.price}</span>
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <button
                onClick={() => setCounterNoticeItem(null)}
                aria-label="Acknowledge counter collection"
                className="hard-shadow w-full border border-foreground bg-primary text-primary-foreground py-2.5 text-xs font-bold uppercase tracking-widest rounded hover:bg-foreground hover:text-background transition-colors"
              >
                Understood · Collect at Counter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Terms Modal */}
      {showTerms && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="w-full max-w-lg border-2 border-foreground bg-background p-6 sm:p-8 hard-shadow space-y-5 rounded-2xl">
            <div className="flex justify-between items-start border-b border-foreground/15 pb-4">
              <div>
                <p className="overline text-primary">Velpula Mess</p>
                <h2 className="text-2xl font-bold tracking-tight">Delivery Terms &amp; Charges</h2>
              </div>
              <button
                onClick={() => setShowTerms(false)}
                aria-label="Close delivery terms modal"
                className="border border-foreground p-1.5 hover:bg-muted rounded"
              >
                <X size={16} />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <p className="text-muted-foreground leading-relaxed">
                We deliver fresh hot meals and delicious dishes directly to your doorstep. Distance-based delivery charges apply:
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {DELIVERY_TERMS.map((t) => (
                  <div
                    key={t.label}
                    className={`p-3 border rounded text-center ${t.highlight
                        ? "border-emerald-600 bg-emerald-50 text-emerald-950 font-bold"
                        : "border-foreground/20 bg-muted/30"
                      }`}
                  >
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{t.label}</div>
                    <div className="font-mono text-sm font-bold mt-1 text-primary">{t.charge}</div>
                  </div>
                ))}
              </div>

              <div className="p-3 border border-amber-600 bg-amber-50 text-amber-900 space-y-1 mt-4 rounded-xl">
                <div className="flex items-center gap-1.5 font-bold uppercase tracking-wider text-[11px]">
                  <Clock size={14} className="text-amber-700" />
                  Late Night Delivery Session (10:00 PM – 6:00 AM)
                </div>
                <p className="text-[11px] leading-relaxed">
                  During late-night hours (10:00 PM to 6:00 AM), <b>only prepaid online orders (UPI, Cards, NetBanking)</b> are accepted. Cash on delivery is unavailable during the night session.
                </p>
              </div>
            </div>

            <div className="pt-3 border-t border-foreground/15 flex justify-end">
              <button
                onClick={() => setShowTerms(false)}
                className="hard-shadow border border-foreground bg-primary text-primary-foreground px-6 py-2.5 text-xs font-semibold uppercase tracking-widest rounded"
              >
                Understood &amp; Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
